import { buildComponent } from '#components'

import { createFocusTrap, type FocusTrap } from 'focus-trap'

const appBodyObservers = new WeakMap<HTMLElement, MutationObserver>()
const showcaseTrapCleanups = new WeakMap<HTMLElement, () => void>()

const selectors = {
  showcaseViewer: '#showcase-viewer',
  showcaseViewerControls: '.showcase-viewer-control',
  showcaseDismiss: 'button.showcase-dismiss',
  showcasePostFooterShareItem: '.showcase-viewer-post-footer-share-item'
}

const ariaLabels = {
  prev: 'Previous slide',
  next: 'Next slide',
  dismiss: 'Close modal'
}

const showcaseShareImageAlts = {
  'facebook-share': 'Follow us on Facebook',
  'instagram-share': 'Follow us on Instagram',
  'email-share': 'Share this page via email.'
} as const

type ShowcaseShareClass = keyof typeof showcaseShareImageAlts

const SHARE_CLASS_ORDER: ShowcaseShareClass[] = ['facebook-share', 'instagram-share', 'email-share']

const labelShowcaseViewerShareItems = (root: HTMLElement) => {
  root.querySelectorAll(selectors.showcasePostFooterShareItem).forEach((item) => {
    if (!(item instanceof HTMLElement)) return

    const shareClass = SHARE_CLASS_ORDER.find((className) => item.classList.contains(className))

    if (!shareClass) return

    const alt = showcaseShareImageAlts[shareClass]

    item.setAttribute('alt', alt)
  })
}

const ensureUntypedButtonsDefaultToButton = (root: HTMLElement) => {
  root.querySelectorAll('button').forEach((btn) => {
    if (!btn.hasAttribute('type')) {
      btn.setAttribute('type', 'button')
    }
  })
}

const labelShowcaseDismissButtons = (root: HTMLElement) => {
  root.querySelectorAll(selectors.showcaseDismiss).forEach((btn) => {
    btn.setAttribute('aria-label', ariaLabels.dismiss)
  })
}

const labelShowcaseViewerControls = (root: HTMLElement) => {
  const controls = root.querySelectorAll(selectors.showcaseViewerControls)

  controls.forEach((el) => {
    if (!(el instanceof HTMLElement)) return

    const button = el.querySelector('button')

    if (!button) return

    if (el.classList.contains('prev')) {
      button.setAttribute('aria-label', ariaLabels.prev)
    } else if (el.classList.contains('next')) {
      button.setAttribute('aria-label', ariaLabels.next)
    }
  })
}

const enhanceShowcaseViewerModal = (root: HTMLElement) => {
  ensureUntypedButtonsDefaultToButton(root)
  labelShowcaseDismissButtons(root)
  labelShowcaseViewerControls(root)
  labelShowcaseViewerShareItems(root)
}

buildComponent({
  name: 'app-component',
  onConnected: (component) => {
    let showcaseTrap: FocusTrap | null = null
    let trappedShowcaseEl: HTMLElement | null = null
    let showcaseWasVisible = false
    let showcaseSyncRaf = 0

    const deactivateShowcaseTrap = () => {
      if (!showcaseTrap) return

      showcaseTrap.deactivate()
      showcaseTrap = null
      trappedShowcaseEl = null
    }

    const isShowcaseDisplayed = (el: HTMLElement) => getComputedStyle(el).display !== 'none'

    const activateShowcaseTrapOn = (rootEl: HTMLElement) => {
      showcaseTrap = createFocusTrap(rootEl, {
        returnFocusOnDeactivate: true,
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
        allowOutsideClick: false,
        fallbackFocus: () => {
          rootEl.tabIndex = -1

          return rootEl
        }
      })
      trappedShowcaseEl = rootEl
      showcaseTrap.activate()
    }

    const syncShowcaseFocusTrap = () => {
      const el = document.querySelector<HTMLElement>(selectors.showcaseViewer)
      const visible = Boolean(el && isShowcaseDisplayed(el))

      if (!visible && !showcaseWasVisible) return

      if (visible && showcaseWasVisible && showcaseTrap && trappedShowcaseEl === el) return

      if (!el) {
        if (showcaseWasVisible) deactivateShowcaseTrap()
        showcaseWasVisible = false

        return
      }

      // Third-party code may replace `#showcase-viewer` without toggling display; re-bind trap to the live node.
      if (visible && showcaseWasVisible && trappedShowcaseEl !== el) {
        enhanceShowcaseViewerModal(el)
        deactivateShowcaseTrap()

        try {
          activateShowcaseTrapOn(el)
        } catch {
          showcaseTrap = null
          trappedShowcaseEl = null
          showcaseWasVisible = false
        }

        return
      }

      if (visible && !showcaseWasVisible) {
        enhanceShowcaseViewerModal(el)
        deactivateShowcaseTrap()

        try {
          activateShowcaseTrapOn(el)
          showcaseWasVisible = true
        } catch {
          showcaseTrap = null
          trappedShowcaseEl = null
          showcaseWasVisible = false
        }

        return
      }

      if (!visible && showcaseWasVisible) {
        deactivateShowcaseTrap()
        showcaseWasVisible = false
      }
    }

    const scheduleShowcaseFocusSync = () => {
      if (showcaseSyncRaf !== 0) return

      showcaseSyncRaf = requestAnimationFrame(() => {
        showcaseSyncRaf = 0
        syncShowcaseFocusTrap()
      })
    }

    showcaseTrapCleanups.set(component, () => {
      deactivateShowcaseTrap()
      showcaseWasVisible = false
    })

    const observer = new MutationObserver(() => {
      scheduleShowcaseFocusSync()

      const reviewEl = component.querySelector('#stamped-reviews-widget') as HTMLElement
      const reviewLinks = reviewEl && reviewEl.querySelectorAll('a.stamped-reviews-link')

      if (reviewLinks && reviewLinks.length > 0) {
        reviewLinks.forEach((link, index) => {
          if (!link.hasAttribute('aria-label')) {
            const label = link.innerHTML ? link.innerHTML : `review item ${index + 1}`

            link.setAttribute('aria-label', label)
          }
        })
      }
    })

    appBodyObservers.set(component, observer)

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    })

    syncShowcaseFocusTrap()
  },
  onDisconnected: (component) => {
    showcaseTrapCleanups.get(component)?.()
    showcaseTrapCleanups.delete(component)
    appBodyObservers.get(component)?.disconnect()
    appBodyObservers.delete(component)
  }
})

declare global {
  interface HTMLElementTagNameMap {
    'app-component': HTMLElement
  }
}
